import { Controller } from "@hotwired/stimulus"
import Autocomplete from "bootstrap5-autocomplete"
import { I18n } from "i18n-js"
import locales from "../locales.json"

// Rich autocomplete for security services package recipients (person registry).
export default class extends Controller {
  static targets = ["name", "id", "hint"]
  static values = { url: String }

  initialize() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || "hu"
  }

  connect() {
    this.initAutocomplete()
  }

  disconnect() {
    this.disposeAutocomplete()
  }

  initAutocomplete() {
    this.disposeAutocomplete()

    this.autocomplete = new Autocomplete(this.nameTarget, {
      server: this.urlValue,
      liveServer: true,
      serverMethod: "GET",
      queryParam: "q",
      serverDataKey: "data",
      labelField: "label",
      valueField: "id",
      searchFields: ["name", "label", "phone", "email", "subtitle"],
      highlightTyped: true,
      highlightClass: "text-primary fw-semibold",
      fullWidth: true,
      fixed: false,
      maximumItems: 12,
      suggestionsThreshold: 1,
      debounceTime: 200,
      onSelectItem: (item) => {
        this.nameTarget.value = item.name || item.label
        this.idTarget.value = item.id
        this.updateHint(item)
        this.autocomplete?.hideSuggestions()
      },
      onRenderItem: (item, label) => {
        const name = this.escapeHtml(item.name || label || "")
        const subtitle = this.escapeHtml(item.subtitle || "")
        const contactBadge = item.needs_contact
          ? `<span class="badge bg-warning text-dark ms-1">${this.i18n.t("security_services.recipients.missing_contact")}</span>`
          : ""
        const externalBadge = item.external
          ? `<span class="badge bg-danger text-white ms-1">${this.i18n.t("security_services.recipients.external_badge")}</span>`
          : ""

        return `<div class="d-flex flex-column py-1">
          <span class="fw-semibold">${name}${externalBadge}${contactBadge}</span>
          ${subtitle ? `<span class="small text-muted">${subtitle}</span>` : ""}
        </div>`
      }
    })

    this.nameTarget.addEventListener("input", this.clearIdWhenTyping)
  }

  disposeAutocomplete() {
    this.nameTarget?.removeEventListener("input", this.clearIdWhenTyping)
    if (this.autocomplete?.dispose) {
      this.autocomplete.dispose()
    }
    this.autocomplete = null
  }

  clearIdWhenTyping = () => {
    if (!this.hasIdTarget) return
    this.idTarget.value = ""
    if (this.hasHintTarget) {
      this.hintTarget.classList.add("d-none")
      this.hintTarget.textContent = ""
    }
  }

  updateHint(item) {
    if (!this.hasHintTarget) return

    const parts = []
    if (item.phone) parts.push(item.phone)
    if (item.email) parts.push(item.email)

    if (item.external) {
      const externalLabel = this.i18n.t("security_services.recipients.external_badge")
      const contactText = parts.length > 0 ? `<span class="text-muted ms-1">${this.escapeHtml(parts.join(" · "))}</span>` : ""
      this.hintTarget.innerHTML = `<span class="badge bg-danger text-white me-1"><i class="bi bi-person-exclamation"></i> ${externalLabel}</span>${contactText}`
      this.hintTarget.classList.remove("d-none", "text-warning", "text-muted")
    } else if (parts.length > 0) {
      this.hintTarget.textContent = parts.join(" · ")
      this.hintTarget.classList.remove("d-none")
      this.hintTarget.classList.add("text-muted")
    } else if (item.needs_contact) {
      this.hintTarget.textContent = this.i18n.t("security_services.recipients.missing_contact")
      this.hintTarget.classList.remove("d-none", "text-muted")
      this.hintTarget.classList.add("text-warning")
    } else {
      this.hintTarget.classList.add("d-none")
    }
  }

  escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
